import { useId, useState } from 'react';
import { ChevronDown, Info, SlidersHorizontal, X } from 'lucide-react';
import { AreaDefinition } from 'modules/arena/reports/model/areas';
import {
  buildChips,
  describeRangeLimits,
  hasActiveFilters,
  optionsFor,
  PERIOD_PRESETS,
  resolvePreset,
} from 'modules/arena/reports/model/filters';
import { Grouping, PeriodPreset, ReportCatalog, ReportFilters, SourceCapabilities } from 'modules/arena/reports/model/types';
import { ReportsController } from 'modules/arena/reports/hooks/useReportsController';
import { Button } from 'shared/components/ui/Button';
import { cn } from 'shared/utils/cn';

const FIELD = 'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

const GROUPING_OPTIONS: { id: Grouping; label: string }[] = [
  { id: 'dia', label: 'Por dia' },
  { id: 'semana', label: 'Por semana' },
  { id: 'mes', label: 'Por mês' },
];

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={cn('flex flex-col gap-1 text-xs font-medium text-ink-secondary', className)}>
      {label}
      {children}
    </label>
  );
}

interface FilterPanelProps {
  controller: ReportsController;
  definition: AreaDefinition;
  catalog: ReportCatalog;
  capabilities: SourceCapabilities;
}

export function FilterPanel({ controller, definition, catalog, capabilities }: FilterPanelProps) {
  const { draft, updateDraft, applyDraft, discardDraft, draftChanged, rangeError, today } = controller;
  const [openOnMobile, setOpenOnMobile] = useState(false);
  const panelId = useId();
  const errorId = useId();

  const setCommon = (patch: Partial<ReportFilters['common']>) => updateDraft({ ...draft, common: { ...draft.common, ...patch } });
  const setSpecific = (key: string, value: string) => updateDraft({ ...draft, specific: { ...draft.specific, [key]: value } });

  function selectPreset(preset: PeriodPreset) {
    const range = resolvePreset(preset, today);
    setCommon(range ? { preset, startDate: range.start, endDate: range.end } : { preset });
  }

  function toggleUnit(unitId: string) {
    const selected = draft.common.unitIds.includes(unitId)
      ? draft.common.unitIds.filter((id) => id !== unitId)
      : [...draft.common.unitIds, unitId];
    setCommon({ unitIds: selected });
  }

  const unitSummary = draft.common.unitIds.length
    ? `${draft.common.unitIds.length} de ${catalog.units.length} selecionada(s)`
    : `Todas (${catalog.units.length})`;

  return (
    <section aria-label="Filtros do relatório" className="rounded-2xl border border-border/50 bg-surface shadow-card">
      <button
        type="button"
        onClick={() => setOpenOnMobile((open) => !open)}
        aria-expanded={openOnMobile}
        aria-controls={panelId}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-ink md:hidden"
      >
        <span className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Filtros
          {draftChanged && <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs text-primary-dark">alterados</span>}
        </span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', openOnMobile && 'rotate-180')} aria-hidden="true" />
      </button>

      <form
        id={panelId}
        onSubmit={(event) => {
          event.preventDefault();
          applyDraft();
        }}
        className={cn('flex-col gap-4 border-t border-border/50 p-4 md:flex md:border-t-0', openOnMobile ? 'flex' : 'hidden')}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <Field label="Período">
            <select className={FIELD} value={draft.common.preset} onChange={(e) => selectPreset(e.target.value as PeriodPreset)}>
              {PERIOD_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Data inicial">
            <input
              type="date"
              className={FIELD}
              value={draft.common.startDate}
              min={capabilities.minDate}
              max={today}
              aria-invalid={Boolean(rangeError)}
              aria-describedby={rangeError ? errorId : undefined}
              onChange={(e) => setCommon({ preset: 'personalizado', startDate: e.target.value })}
            />
          </Field>
          <Field label="Data final">
            <input
              type="date"
              className={FIELD}
              value={draft.common.endDate}
              min={capabilities.minDate}
              max={today}
              aria-invalid={Boolean(rangeError)}
              aria-describedby={rangeError ? errorId : undefined}
              onChange={(e) => setCommon({ preset: 'personalizado', endDate: e.target.value })}
            />
          </Field>

          <div className="flex flex-col gap-1 text-xs font-medium text-ink-secondary">
            <span id={`${panelId}-units`}>Unidades</span>
            <details className="group relative">
              <summary
                aria-labelledby={`${panelId}-units`}
                className={cn(FIELD, 'flex cursor-pointer list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden')}
              >
                <span className="truncate">{unitSummary}</span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
              </summary>
              <fieldset className="absolute z-20 mt-1 w-full min-w-[200px] rounded-lg border border-border bg-surface p-2 shadow-card-hover">
                <legend className="sr-only">Unidades acessíveis</legend>
                {catalog.units.map((unit) => (
                  <label key={unit.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal text-ink hover:bg-chip">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={draft.common.unitIds.includes(unit.id)}
                      onChange={() => toggleUnit(unit.id)}
                    />
                    {unit.label}
                  </label>
                ))}
                <p className="px-2 pt-1 text-[11px] font-normal text-ink-secondary">Nenhuma marcada = todas as que você acessa.</p>
              </fieldset>
            </details>
          </div>

          <Field label="Esporte">
            <select className={FIELD} value={draft.common.sport} onChange={(e) => setCommon({ sport: e.target.value })}>
              <option value="">Todos os esportes</option>
              {catalog.sports.map((sport) => (
                <option key={sport.id} value={sport.id}>
                  {sport.label}
                </option>
              ))}
            </select>
          </Field>

          {definition.supportsGrouping && capabilities.grouping && (
            <Field label="Agrupar">
              <select className={FIELD} value={draft.common.grouping} onChange={(e) => setCommon({ grouping: e.target.value as Grouping })}>
                {GROUPING_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {capabilities.compare && (
            <label className="flex items-end gap-2 pb-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={draft.common.compare}
                onChange={(e) => setCommon({ compare: e.target.checked })}
              />
              Comparar com período anterior
            </label>
          )}
        </div>

        {definition.filters.length > 0 && (
          <div className="grid gap-3 border-t border-border/50 pt-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {definition.filters.map((spec) => (
              <Field key={spec.key} label={spec.label}>
                <select className={FIELD} value={draft.specific[spec.key] ?? ''} onChange={(e) => setSpecific(spec.key, e.target.value)}>
                  {!spec.required && <option value="">Todos</option>}
                  {optionsFor(spec, draft, catalog).map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-border/50 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-ink-secondary">
            {rangeError ? (
              <p id={errorId} role="alert" className="font-medium text-danger">
                {rangeError}
              </p>
            ) : (
              <p className="flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
                {describeRangeLimits(capabilities, today)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {draftChanged && (
              <>
                <span className="text-xs font-medium text-primary-dark">Alterações não aplicadas</span>
                <Button type="button" variant="neutral" className="px-3 py-1.5 text-sm" onClick={discardDraft}>
                  Descartar
                </Button>
              </>
            )}
            <Button type="submit" className="px-4 py-1.5 text-sm" disabled={!draftChanged}>
              Aplicar filtros
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}

export function AppliedChips({ controller, catalog }: { controller: ReportsController; catalog: ReportCatalog }) {
  const { area, applied, removeChip, clearFilters } = controller;
  const chips = buildChips(area, applied, catalog);
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Filtros aplicados">
      <span className="text-xs font-medium text-ink-secondary">Aplicado:</span>
      {chips.map((chip) => (
        <span key={chip.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-surface py-1 pl-3 pr-1 text-xs text-ink">
          {chip.label}
          {chip.removable ? (
            <button
              type="button"
              onClick={() => removeChip(chip.id)}
              aria-label={`Remover filtro ${chip.label}`}
              className="rounded-full p-0.5 text-ink-secondary hover:bg-chip hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          ) : (
            <span className="w-1" />
          )}
        </span>
      ))}
      {hasActiveFilters(area, applied) && (
        <button type="button" onClick={clearFilters} className="text-xs font-semibold text-primary-dark hover:underline">
          Limpar filtros
        </button>
      )}
    </div>
  );
}

export function FilterNotices({ notices, onDismiss }: { notices: string[]; onDismiss: () => void }) {
  if (!notices.length) return null;
  return (
    <div role="status" className="flex items-start justify-between gap-3 rounded-lg border border-primary/30 bg-accent-soft/60 px-3 py-2 text-sm text-ink">
      <ul className="flex flex-col gap-0.5">
        {notices.map((notice) => (
          <li key={notice} className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-dark" aria-hidden="true" />
            {notice}
          </li>
        ))}
      </ul>
      <button type="button" onClick={onDismiss} aria-label="Fechar aviso" className="rounded-md p-1 text-ink-secondary hover:bg-surface">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
